import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

const WIDGET_TAG = "altcha-widget";

// Si el script de ALTCHA (public/altcha.min.js) no registra el Web Component
// en este tiempo, la verificación se considera fallida.
const READY_TIMEOUT_MS = 15000;

// Tiempo máximo para resolver el reto (fetch + proof of work).
const VERIFY_TIMEOUT_MS = 30000;

const STRINGS = JSON.stringify({
  ariaLabel: "Verificación de seguridad",
  error: "Error al verificar el captcha",
  expired: "El captcha ha expirado",
  label: "No soy un robot",
  reload: "Recargar",
  verified: "Verificado",
  verifying: "Verificando...",
  waitPlease: "Por favor, espera...",
});

export const getAltchaChallengeUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return "/altcha-challenge";
  }

  const base = process.env.REACT_APP_ALTCHA_URL || process.env.REACT_APP_URLAPI;

  if (!base) {
    return "/altcha-challenge";
  }

  try {
    return new URL("/altcha-challenge", base).href;
  } catch (error) {
    console.error("URL de captcha inválida:", base, error);
    return "/altcha-challenge";
  }
};

const waitForWidgetDefinition = () => {
  if (customElements.get(WIDGET_TAG)) return Promise.resolve();

  return Promise.race([
    customElements.whenDefined(WIDGET_TAG),
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error("El script de ALTCHA no se cargó.")),
        READY_TIMEOUT_MS,
      ),
    ),
  ]);
};

const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms)),
  ]);

const HANDLER_KEY = "__altchaStateChangeHandler";

/**
 * Captcha ALTCHA invisible.
 *
 * El widget se monta oculto y NO se verifica solo: el reto se resuelve
 * a petición del formulario con `ref.execute()`, justo antes de enviar.
 *
 * Motivo: el backend emite retos con `expires` de ~10 s. Con el widget
 * visible y `refetchonexpire` (default) activo, cada expiración disparaba
 * una nueva verificación automática -> ráfaga de peticiones a
 * /altcha-challenge cada 10 s y el checkbox parpadeando (además de agotar
 * el rate limit de 10 req/min de la API).
 *
 * - `await ref.execute()` -> devuelve el payload (token de un solo uso) o
 *   lanza un Error si la verificación falla. Resetea el widget antes de
 *   cada intento, así el token siempre es nuevo y recién emitido.
 * - `ref.reset()` limpia el estado del widget.
 */
const CaptchaAltcha = forwardRef(function CaptchaAltcha(_props, ref) {
  const widgetRef = useRef(null);
  const lastStateRef = useRef({ state: "unverified", payload: null });

  const attachWidget = useCallback((node) => {
    const previous = widgetRef.current;

    if (previous && previous[HANDLER_KEY]) {
      previous.removeEventListener("statechange", previous[HANDLER_KEY]);
      delete previous[HANDLER_KEY];
    }

    widgetRef.current = node;
    lastStateRef.current = { state: "unverified", payload: null };

    if (!node) return;

    const handleStateChange = (event) => {
      const { state, payload } = event.detail || {};
      lastStateRef.current = { state, payload: payload || null };
    };

    node[HANDLER_KEY] = handleStateChange;
    node.addEventListener("statechange", handleStateChange);
  }, []);

  useEffect(
    () => () => {
      const node = widgetRef.current;

      if (node && node[HANDLER_KEY]) {
        node.removeEventListener("statechange", node[HANDLER_KEY]);
        delete node[HANDLER_KEY];
      }
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        widgetRef.current?.reset?.();
        lastStateRef.current = { state: "unverified", payload: null };
      },
      execute: async () => {
        await waitForWidgetDefinition();

        const widget = widgetRef.current;

        if (!widget || typeof widget.verify !== "function") {
          throw new Error("El captcha no está disponible.");
        }

        // Token de un solo uso: siempre partimos de cero.
        widget.reset?.();
        lastStateRef.current = { state: "unverified", payload: null };

        await withTimeout(
          widget.verify(),
          VERIFY_TIMEOUT_MS,
          "La verificación de seguridad tardó demasiado.",
        );

        const { state, payload } = lastStateRef.current;

        if (state !== "verified" || !payload) {
          throw new Error("No se pudo completar la verificación de seguridad.");
        }

        return payload;
      },
    }),
    [],
  );

  return (
    <div style={{ display: "none" }} aria-hidden="true">
      <altcha-widget
        ref={attachWidget}
        auto="off"
        challengeurl={getAltchaChallengeUrl()}
        disablerefetchonexpire
        hidelogo
        hidefooter
        strings={STRINGS}
      />
    </div>
  );
});

export default CaptchaAltcha;
